import React, { useMemo, useState } from 'react';

import styles from './styles.scss';
import { useTranslation } from 'react-i18next';
import { Toggle } from 'components';
import { Alert, AlertType } from 'components/lds';
import {
  useVisitorManagementBufferTime,
  useVisitorManagementUnvalidatedCredentialHold,
} from 'react-hooks';
import { H4, H5, S, XS } from 'components/typography';
import { Loading } from 'components/general';
import {
  EmailTemplateType,
  OrganizationSettings,
  VisitorConfiguration,
} from 'lane-shared/domains/visitorManagement/types';
import { DefaultOrganizationSettingsData } from 'lane-shared/domains/visitorManagement/hooks/useOrganizationSettingsData';
import { convertToUUID } from 'uuid-encoding';
import Dropdown from 'components/form/Dropdown';
import type { UserType } from 'application-types';

import { VisitorValidationConfirmationModal } from './VisitorValidationConfirmationModal';

type Prop = {
  channel: any;
  onConfigUpdated: (update: VisitorConfiguration) => void;
  config: VisitorConfiguration;
  loading?: boolean;
  user?: UserType;
};

const DEFAULT_15_MINS = 900;

export function VisitorManagementGeneralTab({
  channel,
  onConfigUpdated,
  config,
  loading,
  user,
}: Prop): React.ReactElement {
  const isBufferTimeSettingsEnabled = useVisitorManagementBufferTime();
  const isUnvalidatedCredentialHoldEnabled =
    useVisitorManagementUnvalidatedCredentialHold();
  const { t } = useTranslation();
  const organizationSettings =
    Object.keys(config.organizationSettings!).length !== 0
      ? config.organizationSettings
      : DefaultOrganizationSettingsData;
  const bufferTime = organizationSettings?.bufferTime ?? DEFAULT_15_MINS;
  const visitorValidation = Boolean(organizationSettings?.visitorValidation);
  const isSuperUser = Boolean(user?.isSuperUser);
  const isToggleRestrictedToSuperUser =
    isUnvalidatedCredentialHoldEnabled && !isSuperUser;
  const showVisitorValidationTooltip = isUnvalidatedCredentialHoldEnabled;

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingVisitorValidation, setPendingVisitorValidation] =
    useState(visitorValidation);
  const noEarlyCheckIn = t(
    'web.admin.visitorManagement.configuration.tab.general.bufferTime.noEarlyCheckIn'
  );
  const minsAppend = t(
    'web.admin.visitorManagement.configuration.tab.general.bufferTime.mins'
  );

  const bufferOptions = useMemo(() => {
    const options: { label: string; value: number }[] = [];

    for (let i = 0; i < 9; i++) {
      options.push({
        label: i === 0 ? noEarlyCheckIn : `${i * 15} ${minsAppend}`,
        value: i * 15 * 60,
      });
    }

    return options;
  }, [noEarlyCheckIn, minsAppend]);

  function onFieldUpdate(update: OrganizationSettings) {
    let updatedConfig: VisitorConfiguration = {
      organizationSettings: {
        ...organizationSettings,
        ...update,
        organizationId: convertToUUID(channel._id),
      },
    };

    const { visitorValidation } = update;

    if (visitorValidation !== undefined) {
      // QR is disabled only when visitor validation is on AND FF - isUnvalidatedCredentialHoldEnabled
      // are off. Unvalidated credential holds provide their own gate, so QR
      // stays enabled even with validation on. Equivalent to: !visitorValidation || isUnvalidatedCredentialHoldEnabled
      const qrEnabled = isUnvalidatedCredentialHoldEnabled && visitorValidation ? true : !visitorValidation;
      updatedConfig = {
        ...updatedConfig,
        emailTemplate: {
          qrEnabled,
          organizationId: convertToUUID(channel._id),
          templateType: EmailTemplateType.INVITE,
        },
      };
    }

    onConfigUpdated(updatedConfig);
  }

  function onVisitorValidationToggleClick() {
    if (!isUnvalidatedCredentialHoldEnabled) {
      onFieldUpdate({ visitorValidation: !visitorValidation });
      return;
    }

    if (isToggleRestrictedToSuperUser) {
      return;
    }

    setPendingVisitorValidation(!visitorValidation);
    setIsConfirmModalOpen(true);
  }

  function onVisitorValidationConfirm() {
    onFieldUpdate({ visitorValidation: pendingVisitorValidation });
    setIsConfirmModalOpen(false);
  }

  function onVisitorValidationCancel() {
    setIsConfirmModalOpen(false);
  }

  if (loading) {
    return <Loading className={styles.loading} />;
  }

  return (
    <div className={styles.container}>
      <div>
        <H4 className={styles.title} data-test="visitorGeneralTitle">
          {t('web.admin.visitorManagement.configuration.tab.general.title')}
        </H4>
        <div
          className={styles.description}
          data-test="visitorGeneralDescription"
        >
          <S>
            {t(
              'web.admin.visitorManagement.configuration.tab.general.description.visitorValidation'
            )}
          </S>
        </div>
      </div>
      
      <div className={styles.section}>
        <Toggle
          value={visitorValidation}
          disabled={isToggleRestrictedToSuperUser}
          onChange={onVisitorValidationToggleClick}
          text={t(
            'web.admin.visitorManagement.configuration.tab.general.visitorValidation.title'
          )}
          description={t(
            'web.admin.visitorManagement.configuration.tab.general.visitorValidation.subtitle'
          )}
          TooltipComponent={
            showVisitorValidationTooltip
              ? t(
                  'web.admin.visitorManagement.configuration.tab.general.visitorValidation.tooltip'
                )
              : undefined
          }
          testId="visitorValidationToggle"
        />
        {isToggleRestrictedToSuperUser && (
          <div data-test="visitorValidationCaption">
            <Alert
              type={AlertType.info}
              className={styles.visitorValidationCaption}
            >
              {t(
                visitorValidation
                  ? 'web.admin.visitorManagement.configuration.tab.general.visitorValidation.caption.on'
                  : 'web.admin.visitorManagement.configuration.tab.general.visitorValidation.caption.off'
              )}
            </Alert>
          </div>
        )}
      </div>

      {isUnvalidatedCredentialHoldEnabled && (
        <VisitorValidationConfirmationModal
          isOpen={isConfirmModalOpen}
          turningOn={pendingVisitorValidation}
          onConfirm={onVisitorValidationConfirm}
          onCancel={onVisitorValidationCancel}
        />
      )}

      {isBufferTimeSettingsEnabled && (
        <div className={styles.section}>
          <H5 data-test="visitorBufferTimeTitle">
            {t(
              'web.admin.visitorManagement.configuration.tab.general.bufferTime.title'
            )}
          </H5>
          <XS className={styles.sectionSubtitle}>
            {t(
              'web.admin.visitorManagement.configuration.tab.general.bufferTime.subtitle'
            )}
          </XS>

          <div className={styles.dropdownContainer}>
            <Dropdown
              id="bufferDrop"
              value={bufferTime}
              items={bufferOptions}
              placeholder={noEarlyCheckIn}
              doTranslation={false}
              onValueChange={value => {
                onFieldUpdate({
                  bufferTime: value,
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
